/**
 * ClassDrawer – one class's teachers and students.
 *
 * Every change goes through /school-roster and the list updates from the live
 * roster, so what is shown is what was saved. Enrollment reports each student
 * ("class is full", "from another school") instead of failing silently.
 */

import { useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { BarChart3, Loader2, Pencil, Search, Upload } from 'lucide-react';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '../ui/sheet';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import type { Class } from '../../types/lms';
import type { SchoolRoster } from '../../hooks/useSchoolRoster';
import {
  archiveClass,
  changeClassStudents,
  changeClassTeachers,
  describeStudentChange,
  matchStudents,
  shareClass,
} from '../../services/schoolRosterService';
import { MAX_CLASS_SIZE } from '../../services/classManagementService';
import { parseRosterIdentifiers } from '../../lib/classroom/rosterImport';

const SELECT = 'h-8 rounded-md border border-input bg-background px-2 text-xs text-foreground';
export const classLabel = (c: Pick<Class, 'class_name' | 'section'>) => `${c.class_name}${c.section ? ` ${c.section}` : ''}`;

type Match = { matched: Array<{ input: string; studentId: string; name: string; email: string }>; unmatched: string[] };

export function ClassDrawer({
  cls,
  roster,
  schoolId,
  onEdit,
  onClose,
  mode = 'staff',
  readOnly = false,
  moveTargets,
}: {
  cls: Class;
  roster: SchoolRoster;
  schoolId: string;
  onEdit?: () => void;
  onClose: () => void;
  /**
   * 'staff' edits the class and its teachers; 'teacher' manages the roster of
   * a class they teach and shares it, but the class itself is the school's.
   */
  mode?: 'staff' | 'teacher';
  /** A class shared with this teacher: they see it, they do not change it. */
  readOnly?: boolean;
  /** Classes students may be moved to; a teacher only their own. */
  moveTargets?: Class[];
}) {
  const [busy, setBusy] = useState<string | null>(null);
  const [confirmArchive, setConfirmArchive] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [moveTo, setMoveTo] = useState('');
  const [adding, setAdding] = useState<Set<string>>(new Set());
  const [search, setSearch] = useState('');
  const [addTeacher, setAddTeacher] = useState('');
  const [importText, setImportText] = useState('');
  const [match, setMatch] = useState<Match | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  const studentIds = cls.student_ids ?? [];
  const inClass = useMemo(() => new Set(studentIds), [studentIds]);
  const studentById = useMemo(() => new Map(roster.students.map((s) => [s.id, s])), [roster.students]);
  const members = studentIds.map((id) => studentById.get(id) ?? { id, name: 'Unknown student', email: '', classIds: [] as string[] });
  const seatsLeft = Math.max(0, MAX_CLASS_SIZE - studentIds.length);

  const candidates = useMemo(() => {
    const q = search.trim().toLowerCase();
    return roster.students
      // Pending students are placed by approving them, into the class they asked for.
      .filter((s) => !inClass.has(s.id) && s.approvalStatus === 'approved')
      .filter((s) => !q || `${s.name} ${s.email}`.toLowerCase().includes(q))
      .sort((a, b) => a.classIds.length - b.classIds.length); // students in no class first
  }, [roster.students, inClass, search]);

  const teachers = (cls.teacher_ids ?? []).map((id) => roster.teacherById.get(id) ?? { id, name: 'Unknown teacher', email: '' });
  const addableTeachers = roster.teachers.filter((t) => t.approvalStatus === 'approved' && !(cls.teacher_ids ?? []).includes(t.id));

  const act = async (label: string, work: () => Promise<void>) => {
    setBusy(label);
    try {
      await work();
    } finally {
      setBusy(null);
    }
  };

  const teacherChange = (input: { add?: string[]; remove?: string[]; classTeacherId?: string | null }, done: string) =>
    act('teachers', async () => {
      const r = await changeClassTeachers(cls.id, { schoolId, ...input });
      if (r.ok) toast.success(done);
      else toast.error(r.message || 'The teachers could not be changed.');
    });

  const studentChange = (input: { add?: string[]; remove?: string[]; moveTo?: string | null }, label: string) =>
    act(label, async () => {
      const r = await changeClassStudents(cls.id, { schoolId, ...input });
      if (!r.ok) {
        toast.error(r.message || 'The students could not be changed.');
        return;
      }
      const summary = describeStudentChange([...r.data!.report, ...(r.data!.moved?.report ?? [])]);
      const refused = [...r.data!.report, ...(r.data!.moved?.report ?? [])].some((x) => !['added', 'removed'].includes(x.outcome));
      (refused ? toast.warn : toast.success)(summary);
      setSelected(new Set());
      setAdding(new Set());
      setMatch(null);
    });

  const archive = () =>
    act('archive', async () => {
      const r = await archiveClass(cls.id, schoolId);
      if (!r.ok) {
        toast.error(r.message || 'The class could not be archived.');
        return;
      }
      toast.success(`${classLabel(cls)} archived`);
      onClose();
    });

  const runMatch = () =>
    act('match', async () => {
      // The server takes at most 200 per request; a class holds 30.
      const ids = parseRosterIdentifiers(importText, 200);
      if (ids.length === 0) {
        toast.info('Paste emails or student ids, one per line, or upload a CSV.');
        return;
      }
      const r = await matchStudents(ids, schoolId);
      if (r.ok) setMatch(r.data!);
      else toast.error(r.message || 'The list could not be matched.');
    });

  const onFile = async (file: File | undefined) => {
    if (!file) return;
    if (file.size > 512 * 1024) {
      toast.error('That file is too large; a class list is at most a few hundred rows.');
      return;
    }
    setImportText(await file.text());
    setMatch(null);
  };

  const toggle = (set: Set<string>, id: string) => {
    const next = new Set(set);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    return next;
  };

  const otherClasses = (moveTargets ?? roster.classes).filter((c) => c.id !== cls.id);
  const isStaff = mode === 'staff';
  const sharedWith = (cls.shared_with_teachers ?? []).map((id) => roster.teacherById.get(id) ?? { id, name: 'Unknown teacher', email: '' });
  const shareable = roster.teachers.filter(
    (t) => t.approvalStatus === 'approved' && !(cls.teacher_ids ?? []).includes(t.id) && !(cls.shared_with_teachers ?? []).includes(t.id)
  );
  const [shareWith, setShareWith] = useState('');
  const share = (teacherId: string, on: boolean, name: string) =>
    act('share', async () => {
      const r = await shareClass(cls.id, teacherId, on, schoolId);
      if (r.ok) toast.success(on ? `Shared with ${name}` : `Stopped sharing with ${name}`);
      else toast.error(r.message || 'Sharing could not be changed.');
    });

  return (
    <Sheet open onOpenChange={(o) => !o && !busy && onClose()}>
      <SheetContent className="flex w-full flex-col gap-0 overflow-y-auto sm:max-w-xl">
        <SheetHeader className="space-y-1 text-left">
          <SheetTitle>{classLabel(cls)}</SheetTitle>
          <SheetDescription>
            {[cls.subject, cls.curriculum, cls.academic_year].filter(Boolean).join(' · ') || '—'}
          </SheetDescription>
          <div className="flex flex-wrap gap-2 pt-2">
            {isStaff && onEdit && (
              <Button size="sm" variant="outline" onClick={onEdit} disabled={!!busy}>
                <Pencil className="mr-1.5 h-3.5 w-3.5" /> Edit
              </Button>
            )}
            <Button size="sm" variant="outline" asChild>
              <Link to={`/analytics/classes/${cls.id}`}>
                <BarChart3 className="mr-1.5 h-3.5 w-3.5" /> Performance
              </Link>
            </Button>
            {isStaff && (
              <Button size="sm" variant="ghost" className="ml-auto text-destructive" onClick={() => setConfirmArchive(true)} disabled={!!busy}>
                Archive
              </Button>
            )}
          </div>
          {readOnly && (
            <p className="mt-2 rounded-md bg-muted px-2.5 py-1.5 text-xs text-muted-foreground">
              Shared with you by its teachers: you can see it, and they manage it.
            </p>
          )}
          {confirmArchive && (
            <div className="mt-2 rounded-lg border border-destructive/40 bg-destructive/5 p-3 text-xs">
              <p className="font-medium text-foreground">Archive {classLabel(cls)}?</p>
              <p className="mt-0.5 text-muted-foreground">
                Its {studentIds.length} student(s) and {teachers.length} teacher(s) are taken off it. Past results and sessions are kept.
              </p>
              <div className="mt-2 flex justify-end gap-2">
                <Button size="sm" variant="ghost" onClick={() => setConfirmArchive(false)} disabled={!!busy}>
                  Cancel
                </Button>
                <Button size="sm" variant="destructive" onClick={archive} disabled={!!busy}>
                  {busy === 'archive' && <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />}
                  Archive class
                </Button>
              </div>
            </div>
          )}
        </SheetHeader>

        {/* Teachers */}
        {!isStaff ? (
          <section className="mt-5">
            <h3 className="text-sm font-semibold text-foreground">Teachers</h3>
            <p className="mt-1 text-xs text-muted-foreground">
              {teachers.map((t) => `${t.name}${cls.class_teacher_id === t.id ? ' (class teacher)' : ''}`).join(', ') || '—'}
            </p>
            {!readOnly && (
              <div className="mt-3 rounded-lg border border-border p-3">
                <p className="text-xs font-semibold text-foreground">Shared with</p>
                <p className="mt-0.5 text-[11px] text-muted-foreground">Other teachers can see this class’s students and results, not change them.</p>
                {sharedWith.length > 0 && (
                  <ul className="mt-2 space-y-1">
                    {sharedWith.map((t) => (
                      <li key={t.id} className="flex items-center justify-between text-xs">
                        <span className="truncate text-foreground">{t.name}</span>
                        <button
                          type="button"
                          className="text-muted-foreground hover:text-destructive disabled:opacity-50"
                          disabled={!!busy}
                          onClick={() => share(t.id, false, t.name)}
                        >
                          Stop sharing
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
                <div className="mt-2 flex gap-2">
                  <select className={`${SELECT} flex-1`} value={shareWith} onChange={(e) => setShareWith(e.target.value)} disabled={!!busy}>
                    <option value="">{shareable.length ? 'Share with a teacher…' : 'No other teachers to share with'}</option>
                    {shareable.map((t) => (
                      <option key={t.id} value={t.id}>
                        {t.name}
                      </option>
                    ))}
                  </select>
                  <Button
                    size="sm"
                    variant="secondary"
                    disabled={!shareWith || !!busy}
                    onClick={() =>
                      share(shareWith, true, roster.teacherById.get(shareWith)?.name ?? 'teacher').then(() => setShareWith(''))
                    }
                  >
                    {busy === 'share' ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : 'Share'}
                  </Button>
                </div>
              </div>
            )}
          </section>
        ) : (
        <section className="mt-5">
          <h3 className="text-sm font-semibold text-foreground">Teachers</h3>
          {teachers.length === 0 ? (
            <p className="mt-2 text-xs text-muted-foreground">No teachers yet.</p>
          ) : (
            <ul className="mt-2 divide-y divide-border rounded-lg border border-border">
              {teachers.map((t) => (
                <li key={t.id} className="flex items-center gap-3 px-3 py-2 text-sm">
                  <span className="min-w-0 flex-1 truncate text-foreground">{t.name}</span>
                  <label className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
                    <input
                      type="radio"
                      name={`lead-${cls.id}`}
                      className="h-3.5 w-3.5 accent-primary"
                      checked={cls.class_teacher_id === t.id}
                      disabled={!!busy}
                      onChange={() => teacherChange({ classTeacherId: t.id }, `${t.name} is now class teacher`)}
                    />
                    Class teacher
                  </label>
                  <button
                    type="button"
                    className="text-xs text-muted-foreground hover:text-destructive disabled:opacity-50"
                    disabled={!!busy}
                    onClick={() => teacherChange({ remove: [t.id] }, `${t.name} removed from the class`)}
                  >
                    Remove
                  </button>
                </li>
              ))}
            </ul>
          )}
          <div className="mt-2 flex gap-2">
            <select className={`${SELECT} flex-1`} value={addTeacher} onChange={(e) => setAddTeacher(e.target.value)} disabled={!!busy}>
              <option value="">{addableTeachers.length ? 'Add an approved teacher…' : 'No other approved teachers'}</option>
              {addableTeachers.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name}
                </option>
              ))}
            </select>
            <Button
              size="sm"
              variant="secondary"
              disabled={!addTeacher || !!busy}
              onClick={() => teacherChange({ add: [addTeacher] }, 'Teacher added').then(() => setAddTeacher(''))}
            >
              {busy === 'teachers' ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : 'Add'}
            </Button>
          </div>
        </section>
        )}

        {/* Students */}
        <section className="mt-6">
          <div className="flex items-baseline justify-between">
            <h3 className="text-sm font-semibold text-foreground">Students</h3>
            <span className="text-xs tabular-nums text-muted-foreground">
              {studentIds.length}/{MAX_CLASS_SIZE}
            </span>
          </div>
          <div className="mt-1.5 h-1.5 overflow-hidden rounded-full bg-muted">
            <div
              className={`h-full ${seatsLeft === 0 ? 'bg-destructive' : seatsLeft <= 3 ? 'bg-amber-500' : 'bg-primary'}`}
              style={{ width: `${Math.min(100, (studentIds.length / MAX_CLASS_SIZE) * 100)}%` }}
            />
          </div>

          {members.length > 0 && (
            <>
              <ul className="mt-3 max-h-56 divide-y divide-border overflow-y-auto rounded-lg border border-border">
                {members.map((s) => (
                  <li key={s.id} className="flex items-center gap-3 px-3 py-1.5 text-sm">
                    {!readOnly && (
                      <input
                        type="checkbox"
                        className="h-3.5 w-3.5 accent-primary"
                        checked={selected.has(s.id)}
                        onChange={() => setSelected(toggle(selected, s.id))}
                        aria-label={`Select ${s.name}`}
                      />
                    )}
                    <span className="min-w-0 flex-1 truncate text-foreground">{s.name}</span>
                    <span className="hidden truncate text-xs text-muted-foreground sm:block">{s.email}</span>
                  </li>
                ))}
              </ul>
              {!readOnly && (
              <div className="mt-2 flex flex-wrap items-center gap-2 text-xs">
                <button
                  type="button"
                  className="text-muted-foreground hover:text-foreground"
                  onClick={() => setSelected(selected.size === members.length ? new Set() : new Set(members.map((m) => m.id)))}
                >
                  {selected.size === members.length ? 'Clear selection' : 'Select all'}
                </button>
                <span className="text-muted-foreground">·</span>
                <span className="text-muted-foreground">{selected.size} selected</span>
                <div className="ml-auto flex flex-wrap gap-2">
                  <select className={SELECT} value={moveTo} onChange={(e) => setMoveTo(e.target.value)} disabled={!selected.size || !!busy}>
                    <option value="">Move to…</option>
                    {otherClasses.map((c) => (
                      <option key={c.id} value={c.id}>
                        {classLabel(c)} ({(c.student_ids ?? []).length}/{MAX_CLASS_SIZE})
                      </option>
                    ))}
                  </select>
                  <Button
                    size="sm"
                    variant="secondary"
                    disabled={!selected.size || !moveTo || !!busy}
                    onClick={() => studentChange({ remove: [...selected], moveTo }, 'move').then(() => setMoveTo(''))}
                  >
                    {busy === 'move' ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : 'Move'}
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className="text-destructive"
                    disabled={!selected.size || !!busy}
                    onClick={() => studentChange({ remove: [...selected] }, 'remove')}
                  >
                    {busy === 'remove' ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : 'Remove'}
                  </Button>
                </div>
              </div>
              )}
            </>
          )}

          {!readOnly && (
          <>
          {/* Add from the school */}
          <div className="mt-4 rounded-lg border border-border p-3">
            <p className="text-xs font-semibold text-foreground">Add students</p>
            <div className="relative mt-2">
              <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" aria-hidden />
              <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search the school’s students" className="h-8 pl-8 text-xs" />
            </div>
            <ul className="mt-2 max-h-48 divide-y divide-border overflow-y-auto">
              {candidates.length === 0 ? (
                <li className="py-3 text-center text-xs text-muted-foreground">{search ? 'No matches.' : 'Every student is already in this class.'}</li>
              ) : (
                candidates.slice(0, 200).map((s) => (
                  <li key={s.id} className="flex items-center gap-3 py-1.5 text-xs">
                    <input
                      type="checkbox"
                      className="h-3.5 w-3.5 accent-primary"
                      checked={adding.has(s.id)}
                      onChange={() => setAdding(toggle(adding, s.id))}
                      aria-label={`Add ${s.name}`}
                    />
                    <span className="min-w-0 flex-1 truncate text-foreground">{s.name}</span>
                    <span className="shrink-0 text-muted-foreground">
                      {s.classIds.length === 0
                        ? 'No class'
                        : s.classIds.map((id) => roster.classById.get(id)).filter(Boolean).map((c) => classLabel(c!)).join(', ') || 'No class'}
                    </span>
                  </li>
                ))
              )}
            </ul>
            <div className="mt-2 flex items-center justify-between gap-2">
              <span className="text-[11px] text-muted-foreground">
                {seatsLeft} seat{seatsLeft === 1 ? '' : 's'} left
                {adding.size > seatsLeft ? ' — some will not fit' : ''}
              </span>
              <Button size="sm" disabled={!adding.size || !!busy} onClick={() => studentChange({ add: [...adding] }, 'add')}>
                {busy === 'add' && <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />}
                Add {adding.size || ''}
              </Button>
            </div>
          </div>

          {/* Import a list */}
          <details className="mt-3 rounded-lg border border-border p-3">
            <summary className="cursor-pointer text-xs font-semibold text-foreground">Import a list (CSV or pasted emails)</summary>
            <p className="mt-1 text-[11px] text-muted-foreground">
              Students must already have an account at your school; this matches them, it does not create accounts.
            </p>
            <Textarea
              value={importText}
              onChange={(e) => {
                setImportText(e.target.value);
                setMatch(null);
              }}
              placeholder={'asha@school.in\nravi@school.in'}
              className="mt-2 min-h-[80px] font-mono text-xs"
            />
            <div className="mt-2 flex flex-wrap gap-2">
              <input ref={fileRef} type="file" accept=".csv,.txt,text/csv,text/plain" className="hidden" onChange={(e) => onFile(e.target.files?.[0])} />
              <Button size="sm" variant="outline" onClick={() => fileRef.current?.click()} disabled={!!busy}>
                <Upload className="mr-1.5 h-3.5 w-3.5" /> Upload CSV
              </Button>
              <Button size="sm" variant="secondary" onClick={runMatch} disabled={!importText.trim() || !!busy}>
                {busy === 'match' && <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />}
                Match
              </Button>
            </div>
            {match && (
              <div className="mt-3 space-y-2 text-xs">
                <p className="text-foreground">
                  {match.matched.length} matched
                  {match.unmatched.length ? `, ${match.unmatched.length} not found` : ''}
                </p>
                {match.unmatched.length > 0 && (
                  <p className="break-words text-muted-foreground">Not found: {match.unmatched.slice(0, 20).join(', ')}{match.unmatched.length > 20 ? '…' : ''}</p>
                )}
                {match.matched.length > 0 && (
                  <Button
                    size="sm"
                    disabled={!!busy}
                    onClick={() => studentChange({ add: match.matched.map((m) => m.studentId) }, 'import')}
                  >
                    {busy === 'import' && <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />}
                    Enroll {match.matched.length} matched
                  </Button>
                )}
              </div>
            )}
          </details>
          </>
          )}
        </section>
      </SheetContent>
    </Sheet>
  );
}
